import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, View } from 'react-native';
import { BMPrimitives, useBMTheme } from '../../theme';
import BMDotLoader from '../DotLoader';
import BMText from '../Text/BMText';
import { useButtonStyle } from './utils/useButtonStyle';
import { Svg, Path } from 'react-native-svg';
import { DRNUtil } from '@traveloka/district-util';
class PathComponent extends React.Component {
    render() {
        return <Path {...this.props}/>;
    }
}
const BMButton = (props) => {
    const { BMComponents } = useBMTheme();
    const { label, onPress, TrailingIcon, role = 'PRIMARY', size = 'NORMAL', disabled, loading = false, stretchable, timerDuration, buttonStyle: customButtonStyle = null, iconStyle, onTimerEnd, testID, htmlAttr = {}, } = props;
    const AnimatedPath = Animated.createAnimatedComponent(PathComponent);
    const [isFocused, setIsFocused] = useState(false);
    const [isTimerShown, setIsTimerShown] = useState(false);
    const [styles] = useState(() => buttonStyles(BMComponents));
    const timeoutRef = useRef(0);
    const timerTimeoutRef = useRef(0);
    const pressableRef = useRef(null);
    useEffect(() => {
        // Add custom button props for SEO
        const htmlAttrKeys = Object.keys(htmlAttr);
        if ((DRNUtil.isWeb() || process.env.NODE_ENV === 'test') && pressableRef.current && htmlAttrKeys.length > 0) {
            htmlAttrKeys.forEach((key) => {
                var _a, _b;
                // Adding optional chaining at the setAttribute because it's not supported in RNW
                (_b = (_a = pressableRef.current).setAttribute) === null || _b === void 0 ? void 0 : _b.call(_a, key, htmlAttr[key]);
            });
        }
        return () => clearTimeout(timeoutRef.current);
    }, []);
    // calculating button size for timer
    const [buttonWidth, setButtonWidth] = useState(0);
    const [buttonHeight, setButtonHeight] = useState(0);
    const onLayout = (event) => {
        const { width, height } = event.nativeEvent.layout;
        setButtonWidth(width);
        setButtonHeight(height);
    };
    const timerWidth = buttonWidth + 4;
    const timerHeight = buttonHeight + 4;
    const timerCircumference = timerHeight * 2 + timerWidth * 2;
    // base styling
    const { activeBackgroundColor, textVariant, color, backgroundColor, ...buttonStyle } = useButtonStyle(role, size, customButtonStyle);
    const baseButtonStyle = {
        ...buttonStyle,
        backgroundColor: disabled || loading ? BMComponents.BMUiButtonBgcolorDisabled : backgroundColor,
    };
    let buttonTextStyle = {
        ...textVariant,
        color: disabled || loading ? BMComponents.BMUiButtonInkDisabled : color,
    };
    const iconColor = disabled || loading
        ? { accentColor: BMComponents.BMUiButtonInkDisabled, fillColor: BMComponents.BMUiButtonInkDisabled }
        : {
            accentColor: color,
            fillColor: color,
            ...iconStyle,
        };
    // animations
    const timerAnimation = useRef(new Animated.Value(0)).current;
    const backgroundColorAnimation = useRef(new Animated.Value(0)).current;
    const borderColorAnimation = useRef(new Animated.Value(0)).current;
    const timerOpacityAnimation = useRef(new Animated.Value(0)).current;
    const backgroundColorInterpolation = backgroundColorAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [baseButtonStyle.backgroundColor, activeBackgroundColor],
    });
    const borderColorInterpolation = borderColorAnimation.interpolate({
        // [pressed color, normal color, focused color]
        inputRange: [-1, 0, 1],
        outputRange: [BMComponents.BMStyleLineBordercolorActive, 'transparent', BMComponents.BMStyleLineBordercolorFocus],
    });
    const timerStrokeInterpolation = timerAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -timerCircumference],
    });
    const timerOpacity = timerOpacityAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    });
    const isIconOnly = !label;
    const strechedButtonIconStyle = isIconOnly ? styles.iconOnlyStrechedBtn : styles.iconOnStrechtedBtn;
    const handleFadeOutTimer = () => {
        Animated.timing(timerOpacityAnimation, {
            toValue: 0,
            duration: BMPrimitives.BMAnimation.duration.quick,
            useNativeDriver: true,
        }).start(() => {
            setIsTimerShown(false);
            timerAnimation.setValue(0);
        });
    };
    useEffect(() => {
        if (timerDuration) {
            setIsTimerShown(true);
            const parsedTimerDuration = timerDuration ? timerDuration * 1000 : 2500;
            Animated.parallel([
                Animated.timing(timerOpacityAnimation, {
                    toValue: 1,
                    duration: BMPrimitives.BMAnimation.duration.quick,
                    useNativeDriver: true,
                }),
                Animated.timing(timerAnimation, {
                    toValue: 1,
                    duration: parsedTimerDuration,
                    useNativeDriver: false,
                }),
            ], { stopTogether: false }).start();
            // We need to separate this in setTimeout instead of start because sometimes in android the animated did not wait for all animation to finish
            timerTimeoutRef.current = setTimeout(() => handleOnTimeout(true), Math.max(parsedTimerDuration, BMPrimitives.BMAnimation.duration.quick));
        }
    }, [timerDuration]);
    useEffect(() => {
        // Reset animation value so interpolation will use the new colors
        backgroundColorAnimation.setValue(0);
        borderColorAnimation.setValue(0);
    }, [baseButtonStyle.backgroundColor, activeBackgroundColor]);
    // Path for the timer
    // setting so that it starts at the center
    const timerPath = `M${timerWidth / 2 + 1},1 h${timerWidth / 2 - timerHeight / 2} a${timerHeight / 2},${timerHeight / 2} 0 0 1 ${timerHeight / 2},${timerHeight / 2} a${timerHeight / 2},${timerHeight / 2} 0 0 1 -${timerHeight / 2},${timerHeight / 2}  h-${timerWidth - timerHeight} a${timerHeight / 2},${timerHeight / 2} 0 0 1 -${timerHeight / 2},-${timerHeight / 2} a${timerHeight / 2},${timerHeight / 2} 0 0 1 ${timerHeight / 2},-${timerHeight / 2} h${timerWidth / 2 - timerHeight / 2}  z`;
    // handlers
    const handlePressIn = () => {
        Animated.parallel([
            Animated.timing(backgroundColorAnimation, {
                toValue: 1,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
            }),
            Animated.timing(borderColorAnimation, {
                toValue: -1,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
            }),
        ]).start();
        if (timerDuration) {
            timerAnimation.stopAnimation(() => {
                clearTimeout(timerTimeoutRef.current);
                // Since we moved the timer.start() animation callback logic to separate timeout, we need to call it manually here
                handleOnTimeout();
                handleFadeOutTimer();
            });
        }
    };
    const handlePressOut = () => {
        Animated.parallel([
            Animated.timing(backgroundColorAnimation, {
                toValue: 0,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
            }),
            Animated.timing(borderColorAnimation, {
                toValue: 0,
                duration: BMPrimitives.BMAnimation.duration.quick,
                useNativeDriver: false,
            }),
        ]).start();
    };
    const handleHoverIn = () => {
        Animated.timing(backgroundColorAnimation, {
            toValue: 1,
            duration: BMPrimitives.BMAnimation.duration.quick,
            useNativeDriver: false,
        }).start();
    };
    const handleHoverOut = () => {
        Animated.timing(backgroundColorAnimation, {
            toValue: 0,
            duration: BMPrimitives.BMAnimation.duration.quick,
            useNativeDriver: false,
        }).start();
    };
    const handleFocusBorderAnimation = () => {
        Animated.timing(borderColorAnimation, {
            toValue: isFocused ? 0 : 1,
            duration: BMPrimitives.BMAnimation.duration.quick,
            useNativeDriver: false,
        }).start();
        setIsFocused(!isFocused);
    };
    const handleFocus = () => {
        handleFocusBorderAnimation();
    };
    const handleBlur = () => {
        handleFocusBorderAnimation();
    };
    const handleOnTimeout = (invokePressIn = false) => {
        onTimerEnd === null || onTimerEnd === void 0 ? void 0 : onTimerEnd();
        if (invokePressIn) {
            handlePressIn();
        }
        onPress();
        timeoutRef.current = setTimeout(handlePressOut, BMPrimitives.BMAnimation.duration.quick);
    };
    return (<Pressable onHoverIn={handleHoverIn} onHoverOut={handleHoverOut} onLayout={onLayout} onPress={onPress} onPressIn={handlePressIn} onPressOut={() => {
            timeoutRef.current = setTimeout(handlePressOut, BMPrimitives.BMAnimation.duration.quick);
        }} onFocus={handleFocus} onBlur={handleBlur} style={[
            styles.pressable,
            // @ts-ignore 'fit-content' can't be value of width anymore as the type became more specific now
            DRNUtil.isWeb()
                ? { width: stretchable ? '100%' : 'fit-content' }
                : { alignSelf: stretchable ? 'stretch' : 'flex-start' },
        ]} disabled={disabled || loading} testID={testID} ref={pressableRef}>
      <Animated.View style={[styles.container, baseButtonStyle, { backgroundColor: backgroundColorInterpolation }]}>
        {loading ? (<BMDotLoader color={BMComponents.BMUiButtonInkDisabled}/>) : (<>
            {label && (<BMText style={[buttonTextStyle, !isIconOnly && { justifyContent: 'center' }]} numberOfLines={1} ellipsizeMode="tail" selectable={false}>
                {label}
              </BMText>)}
            {TrailingIcon && (<View style={stretchable
                    ? [strechedButtonIconStyle, { paddingRight: isIconOnly ? 0 : baseButtonStyle.paddingHorizontal }]
                    : { marginLeft: isIconOnly ? 0 : BMPrimitives.BMSize.xs }}>
                {<TrailingIcon width={size === 'LARGE' ? 24 : 16} height={size === 'LARGE' ? 24 : 16} {...iconColor}/>}
              </View>)}
          </>)}
      </Animated.View>
      {/* Pseudo border  */}
      <Animated.View style={[
            styles.border,
            {
                minWidth: buttonWidth + 4,
                height: buttonHeight + 4,
                borderWidth: 2,
                borderColor: borderColorInterpolation,
            },
        ]}/>

      {/* Timer */}
      {isTimerShown && (<Animated.View style={{
                position: 'absolute',
                opacity: timerOpacity,
                top: -3,
                left: -3,
            }}>
          <Svg width={buttonWidth + 8} height={buttonHeight + 8}>
            {/* BG path  */}
            <Path d={timerPath} fill={'transparent'} stroke={BMPrimitives.BMBrand.accentDark} strokeWidth="2"/>
            {/* animated path  */}
            <AnimatedPath d={timerPath} stroke={BMPrimitives.BMBrand.accent} strokeWidth="2" fill={'transparent'} strokeDasharray={timerCircumference} strokeDashoffset={timerStrokeInterpolation} strokeLinecap={'round'}/>
          </Svg>
        </Animated.View>)}
    </Pressable>);
};
const buttonStyles = (BMComponents) => StyleSheet.create({
    pressable: {
        // @ts-ignore
        outlineStyle: 'none',
    },
    container: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: BMComponents.BMUiButtonRadius,
        borderWidth: BMPrimitives.BMStroke.lg,
    },
    border: {
        position: 'absolute',
        top: -2,
        left: -2,
        borderRadius: BMComponents.BMUiButtonRadius,
        zIndex: -10,
    },
    iconOnStrechtedBtn: {
        position: 'absolute',
        right: 0,
        zIndex: 1,
        overflow: 'hidden',
    },
    iconOnlyStrechedBtn: {
        zIndex: 1,
    },
});
export default BMButton;
